import React from 'react';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { ClickAwayListener, Grid, SvgIcon, Typography } from '@mui/material';
import ErrorIconAlert from 'src/icons/untitled-ui/duocolor/month-end-alerts/errorIcon';
import WarningIconAlert from 'src/icons/untitled-ui/duocolor/month-end-alerts/warningIcon';
import IgnoreIconAlert from 'src/icons/untitled-ui/duocolor/month-end-alerts/ignoreIcon';
import { RedirectByActionAlert } from '../RedirectByActionAlert';
import { RefreshLaborAlertAction } from './RefreshLaborAlertAction';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { billingBlockerAlertsTreatment, treatmentButNoLaborTreatment } from 'src/config';
import { billingBlockerGuidance, withDiscipline } from './billingBlockerAlerts';
import { TableSortLabel } from '@mui/material';

type SortOptions = {
  sortBy: 'title' | 'theme' | 'therapist' | 'patientName' | 'document' | 'dueDate';
  sortDirection: 'asc' | 'desc';
};
interface PatientListTableProps {
  count?: number;
  items?: any;
  onPageChange?: (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onRowClick?: (event: ChangeEvent<HTMLInputElement>) => void;
  page?: number;
  rowsPerPage?: number;
  sortOptions?: SortOptions;
  setSortOptions?: React.Dispatch<React.SetStateAction<SortOptions>>;
  setRowPerPage?: React.Dispatch<React.SetStateAction<number>>;
  setPageNo? : React.Dispatch<React.SetStateAction<number>>;
  /** BOPS-126: refetch the alert list after a per-row Refresh clears/rebuilds an alert. */
  onAlertRefreshed?: () => void;
}

const SquareIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M10 13.3333V10M10 6.66667H10.0083M6.5 17.5H13.5C14.9001 17.5 15.6002 17.5 16.135 17.2275C16.6054 16.9878 16.9878 16.6054 17.2275 16.135C17.5 15.6002 17.5 14.9001 17.5 13.5V6.5C17.5 5.09987 17.5 4.3998 17.2275 3.86502C16.9878 3.39462 16.6054 3.01217 16.135 2.77248C15.6002 2.5 14.9001 2.5 13.5 2.5H6.5C5.09987 2.5 4.3998 2.5 3.86502 2.77248C3.39462 3.01217 3.01217 3.39462 2.77248 3.86502C2.5 4.3998 2.5 5.09987 2.5 6.5V13.5C2.5 14.9001 2.5 15.6002 2.77248 16.135C3.01217 16.6054 3.39462 16.9878 3.86502 17.2275C4.3998 17.5 5.09987 17.5 6.5 17.5Z"
        stroke="#6C737F"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
function capitalizeEachWord(text: any) {
  if (!text) {
    return text;
  }
  return text
    .split(' ')
    .map((word: any) => {
      if (word === 'PT' || word === 'OT' || word === 'SLP' || word === 'of' || word === 'SSN')
        return word;
      else return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

const LinkIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.00016 10C5.00016 9.53976 5.37326 9.16667 5.8335 9.16667L14.1668 9.16667C14.6271 9.16667 15.0002 9.53976 15.0002 10C15.0002 10.4602 14.6271 10.8333 14.1668 10.8333L5.8335 10.8333C5.37326 10.8333 5.00016 10.4602 5.00016 10Z"
        fill="#6C737F"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.8335 6.66667C3.99255 6.66667 2.50016 8.15905 2.50016 10C2.50016 11.8409 3.99255 13.3333 5.8335 13.3333H7.50016C7.9604 13.3333 8.3335 13.7064 8.3335 14.1667C8.3335 14.6269 7.9604 15 7.50016 15H5.8335C3.07207 15 0.833496 12.7614 0.833496 10C0.833496 7.23858 3.07207 5 5.8335 5H7.50016C7.9604 5 8.3335 5.3731 8.3335 5.83333C8.3335 6.29357 7.9604 6.66667 7.50016 6.66667H5.8335Z"
        fill="#6C737F"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.6668 5.83333C11.6668 5.3731 12.0399 5 12.5002 5H14.1668C16.9283 5 19.1668 7.23858 19.1668 10C19.1668 12.7614 16.9283 15 14.1668 15H12.5002C12.0399 15 11.6668 14.6269 11.6668 14.1667C11.6668 13.7064 12.0399 13.3333 12.5002 13.3333H14.1668C16.0078 13.3333 17.5002 11.8409 17.5002 10C17.5002 8.15905 16.0078 6.66667 14.1668 6.66667H12.5002C12.0399 6.66667 11.6668 6.29357 11.6668 5.83333Z"
        fill="#6C737F"
      />
    </svg>
  );
};

const ListingTable: FC<PatientListTableProps> = (props) => {
  const {
    count = 0,
    items = [],
    onPageChange = () => {},
    onRowsPerPageChange,
    page = 0,
    rowsPerPage = 0,
    sortOptions,
    setSortOptions,
    setRowPerPage,
    setPageNo,
    onAlertRefreshed,
  } = props;

  // BOPS-126: the per-row Refresh action is only offered for Treatment-but-No-Labor errors, and only
  // when the enterprise feature is on (same ENTERPRISE split key the backend gates on).
  const [treatmentButNoLaborToggle] = useFeatureToggleWhenReady(
    treatmentButNoLaborTreatment,
    'ENTERPRISE'
  );
  const isRefreshActionEnabled = treatmentButNoLaborToggle === true;

  // BOPS-432: a billing blocker alert gets an info icon and its fix guidance instead of the redirect,
  // only while the billing blocker flag is on (off, the alert looks as it always has).
  const [billingBlockerToggle] = useFeatureToggleWhenReady(billingBlockerAlertsTreatment, 'ENTERPRISE');
  const blockerGuidanceFor = (item: any): string | null =>
    billingBlockerToggle === true ? billingBlockerGuidance(item?.key) : null;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [getCurrentItem, setGetCurrentItem] = React.useState<any>(null);

  const handleClick = (event: any, item: any) => {
    setAnchorEl(event.currentTarget);
    setGetCurrentItem(item);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setGetCurrentItem(null);
  };

  const open = Boolean(anchorEl);
  const DisplayMessage: React.FC<{ data: any }> = ({ data }) => {
    // BOPS-126: a Treatment-but-No-Labor alert set to Error gets a Refresh action instead of the
    // redirect — clicking it recomputes that therapist/day and clears the alert if labor was logged.
    const showRefreshAction =
      isRefreshActionEnabled && data?.key === 'MISSING_LABOR_LOG' && data?.type === 'ERROR';
    const guidance = blockerGuidanceFor(data);
    if (guidance) {
      return (
        <Typography
          variant="body2"
          color={'text.secondary'}
          data-testid={`closeout-alert-guidance-${data?.uuid}`}
        >
          {guidance}
        </Typography>
      );
    }
    return (
      <>
        <Typography
          variant="body2"
          color={'text.secondary'}
        >
          {data.info}
        </Typography>
        {showRefreshAction ? (
          <RefreshLaborAlertAction
            data={data}
            onRefreshed={() => {
              onAlertRefreshed?.();
              handleClose();
            }}
          />
        ) : (
          data.isActionReq && <RedirectByActionAlert data={data} />
        )}
      </>
    );
  };

  const renderIconBasedOnType = (type: string | undefined) => {
    if (type === 'ERROR') {
      return <ErrorIconAlert />;
    } else if (type === 'WARNING') {
      return <WarningIconAlert />;
    } else {
      return <IgnoreIconAlert />;
    }
  };

  const handleSort = (
    sortoption: SortOptions['sortBy'],
    direction: SortOptions['sortDirection']
  ) => {
    if(setSortOptions) {
      setPageNo && setPageNo(0);
      setRowPerPage && setRowPerPage(20);
      setSortOptions({
        sortBy: sortoption,
        sortDirection: direction,
      });
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={true}
                direction={sortOptions?.sortBy === 'title' ? sortOptions?.sortDirection : 'desc'}
                onClick={() =>
                  handleSort('title', sortOptions?.sortDirection === 'asc' ? 'desc' : 'asc')
                }
              >
                TITLE
              </TableSortLabel>{' '}
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={true}
                direction={sortOptions?.sortBy === 'theme' ? sortOptions?.sortDirection : 'desc'}
                onClick={() =>
                  handleSort('theme', sortOptions?.sortDirection === 'asc' ? 'desc' : 'asc')
                }
              >
                THEME{' '}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={true}
                direction={
                  sortOptions?.sortBy === 'therapist' ? sortOptions?.sortDirection : 'desc'
                }
                onClick={() =>
                  handleSort('therapist', sortOptions?.sortDirection === 'asc' ? 'desc' : 'asc')
                }
              >
                THERAPIST{' '}
              </TableSortLabel>{' '}
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={true}
                direction={
                  sortOptions?.sortBy === 'patientName' ? sortOptions?.sortDirection : 'desc'
                }
                onClick={() =>
                  handleSort('patientName', sortOptions?.sortDirection === 'asc' ? 'desc' : 'asc')
                }
              >
                PATIENT NAME
              </TableSortLabel>
            </TableCell>
            <TableCell>
              {' '}
              <TableSortLabel
                active={true}
                direction={sortOptions?.sortBy === 'document' ? sortOptions?.sortDirection : 'desc'}
                onClick={() =>
                  handleSort('document', sortOptions?.sortDirection === 'asc' ? 'desc' : 'asc')
                }
              >
                {' '}
                Document
              </TableSortLabel>
            </TableCell>
            <TableCell>
              {' '}
              <TableSortLabel
                active={true}
                direction={sortOptions?.sortBy === 'dueDate' ? sortOptions?.sortDirection : 'desc'}
                onClick={() =>
                  handleSort('dueDate', sortOptions?.sortDirection === 'asc' ? 'desc' : 'asc')
                }
              >
                Due Date
              </TableSortLabel>{' '}
            </TableCell>

            <TableCell sx={{ width: '20px' }}>ACTION</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Box
                  textAlign="center"
                  p={3}
                >
                  <Typography
                    color={'text.secondary'}
                    variant="subtitle2"
                  >
                    No Records Found
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item: any) => {
              return (
                <TableRow
                  hover
                  key={item?.uuid}
                >
                  <TableCell>
                    <Stack
                      alignItems="center"
                      direction="row"
                      gap="1rem"
                    >
                      <SvgIcon>{renderIconBasedOnType(item?.type)}</SvgIcon>
                      <Typography>{capitalizeEachWord(item?.alertMessage) || '-'}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{capitalizeEachWord(item?.theme?.replace('_', ' ')) || '-'}</TableCell>
                  {/* <TableCell>
                    {item?.alertActionData?.formSubmission?.documentType ? (
                      item?.alertActionData?.formSubmission?.documentType?.length < 15 ? (
                        `Documentation | ${item?.alertActionData?.formSubmission?.documentType}`
                      ) : (
                        <Tooltip
                          title={`Documentation | ${item?.alertActionData?.formSubmission?.documentType}`}
                        >
                          <span>
                            Documentation |{' '}
                            {item?.alertActionData?.formSubmission?.documentType?.slice(0, 15)}...
                          </span>
                        </Tooltip>
                      )
                    ) : (
                      '-'
                    )}
                  </TableCell> */}
                  <TableCell>{item?.ownerName || '-'}</TableCell>
                  <TableCell>{item?.patientName || '-'}</TableCell>
                  <TableCell>
                    {item?.documentType
                      ? withDiscipline(
                          capitalizeEachWord(item?.documentType?.split('_').join(' ')),
                          item?.discipline
                        )
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {/* {item?.status ? <Label {...statusLabelProps[item?.status]} /> : '-'} */}
                    {item?.dueDate ? (
                      <Box>{item?.dueDate}</Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        -
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ position: 'relative' }}
                  >
                    {item.isActionReq && !blockerGuidanceFor(item) ? (
                      <Grid
                        data-testid={`closeout-alert-action-${item.uuid}`}
                        onMouseEnter={(event: any) => handleClick(event, item)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <LinkIcon />
                      </Grid>
                    ) : (
                      <Grid
                        data-testid={`closeout-alert-action-${item.uuid}`}
                        onMouseEnter={(event: any) => handleClick(event, item)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SquareIcon />
                      </Grid>
                    )}

                    {open && getCurrentItem?.uuid === item.uuid && (
                      <ClickAwayListener onClickAway={handleClose}>
                        <Box
                          sx={{
                            borderRadius: '12px',
                            background: '#FFF',
                            boxShadow: '0px 3px 14px 0px rgba(0, 0, 0, 0.08)',
                            width: '268px',
                            position: 'absolute',
                            right: 0,
                            zIndex: 999,
                            padding: '16px',
                            textAlign: 'left',
                          }}
                          onMouseLeave={handleClose}
                        >
                          {<DisplayMessage data={item} />}
                        </Box>
                      </ClickAwayListener>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {items.length !== 0 && (
        <TablePagination
          component="div"
          count={count}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20, 40, 60]}
        />
      )}
    </Box>
  );
};

export default ListingTable;
